import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

type LatLng = { lat: number; lng: number };

function parseRing(geojson: string): LatLng[] {
  if (!geojson?.trim()) return [];
  try {
    const data = JSON.parse(geojson);
    const geom = data?.type === 'Feature' ? data.geometry : data;
    const coords = geom?.coordinates?.[0];
    if (!Array.isArray(coords)) return [];
    // GeoJSON is [lng, lat]; drop closing duplicate
    const pts = coords.map((c: number[]) => ({ lat: c[1], lng: c[0] }));
    if (pts.length > 1) {
      const a = pts[0];
      const b = pts[pts.length - 1];
      if (a.lat === b.lat && a.lng === b.lng) pts.pop();
    }
    return pts;
  } catch {
    return [];
  }
}

function toGeoJson(points: LatLng[]): string {
  if (points.length < 3) return '';
  const ring = points.map((p) => [p.lng, p.lat]);
  ring.push([points[0].lng, points[0].lat]);
  return JSON.stringify({ type: 'Polygon', coordinates: [ring] });
}

export default function ZonePolygonMap({
  value,
  onChange,
}: {
  value: string;
  onChange: (geojson: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const pointsRef = useRef<LatLng[]>([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [points, setPoints] = useState<LatLng[]>(() => parseRing(value));
  const [ready, setReady] = useState(false);

  // Sync external value (e.g. when editing another zone)
  useEffect(() => {
    const next = parseRing(value);
    const same =
      next.length === pointsRef.current.length &&
      next.every((p, i) => p.lat === pointsRef.current[i]?.lat && p.lng === pointsRef.current[i]?.lng);
    if (!same) {
      pointsRef.current = next;
      setPoints(next);
    }
  }, [value]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [53.7632, -2.7031], // Preston / NW England default
      zoom: 11,
      scrollWheelZoom: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    const layer = L.layerGroup().addTo(map);
    layerRef.current = layer;
    mapRef.current = map;

    map.on('click', (e: L.LeafletMouseEvent) => {
      const next = [...pointsRef.current, { lat: e.latlng.lat, lng: e.latlng.lng }];
      pointsRef.current = next;
      setPoints(next);
      onChangeRef.current(toGeoJson(next));
    });

    setReady(true);
    setTimeout(() => map.invalidateSize(), 80);
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (!points.length) return;

    points.forEach((p, i) => {
      L.circleMarker([p.lat, p.lng], {
        radius: 6,
        color: '#0f766e',
        fillColor: '#14b8a6',
        fillOpacity: 1,
        weight: 2,
      })
        .bindTooltip(`${i + 1}`, { permanent: false })
        .addTo(layer);
    });

    if (points.length >= 2) {
      L.polyline(
        points.map((p) => [p.lat, p.lng] as [number, number]),
        { color: '#0f766e', weight: 2 },
      ).addTo(layer);
    }
    if (points.length >= 3) {
      L.polygon(
        points.map((p) => [p.lat, p.lng] as [number, number]),
        { color: '#0f766e', weight: 2, fillColor: '#14b8a6', fillOpacity: 0.25 },
      ).addTo(layer);
      try {
        map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number])), {
          padding: [24, 24],
          maxZoom: 14,
        });
      } catch {
        /* ignore */
      }
    }
  }, [points, ready]);

  const undo = () => {
    const next = pointsRef.current.slice(0, -1);
    pointsRef.current = next;
    setPoints(next);
    onChangeRef.current(toGeoJson(next));
  };

  const clear = () => {
    pointsRef.current = [];
    setPoints([]);
    onChangeRef.current('');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
        <span>Click the map to add corners (need 3+). Drag to pan.</span>
        <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={undo} disabled={points.length === 0}>
          Undo point
        </button>
        <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={clear} disabled={points.length === 0}>
          Clear
        </button>
        <span className="text-gray-400">{points.length} point{points.length === 1 ? '' : 's'}</span>
      </div>
      <div ref={containerRef} className="h-64 sm:h-80 w-full rounded-xl border border-gray-200 overflow-hidden z-0" />
    </div>
  );
}
